import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { Button, ButtonInline } from '@remote-com/norma/core/button';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { contentfulFieldHasContent } from 'remote-com-shared/domains/contentful/helpers';
import {
  imageEntryToPrismicFormat,
  isContentfulRichText,
  RenderFieldText,
  universalFieldHasContent,
} from 'remote-com-shared/domains/contentful/prismicBridge';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import { ThemeProvider } from 'styled-components';

import { getFields } from '../../domains/hubspot/fields';
import { handleFormRedirect } from '../../domains/hubspot/helpers';
import { useHubspotForm } from '../../domains/hubspot/hooks';
import { dataLayerPush } from '../CookieScripts/helpers';
import { HubspotFormModalButton } from '../HubspotForm';
import { getCurrentURL, registerLead } from '../ModalForm/helpers/services';
import { CaptchaWrapper, FormError } from '../ModalForm/styles';

import {
  BannerBody,
  BannerContent,
  BannerDescription,
  BannerHead,
  BannerPosition,
  BannerTitle,
  BannerWrapper,
  CTAButton,
  FormWrapper,
  getIdForBanner,
  IllustrationWrapper,
  SuccessMessage,
  SuccessWrapper,
} from './sharedStyles';

export function GatedContent(props) {
  // slice is defined for light backwards compatibility with panda which uses prismic slices
  const { slice, context } = transformProperties(props);
  let { entry } = props;

  if (slice && !entry) {
    entry = transformProperties(slice.primary);
  }
  const {
    buttonLabel,
    file,
    illustration: illustrationSource,
    gatedFormId,
    captcha,
    formAsModal,
  } = entry;

  const {
    description: descriptionSource,
    image: prismicImage,
    backgroundImage: backgroundImageSource = prismicImage,
    title,
  } = context.banner || transformProperties(context.primary.banner);
  const { size, anchorId } = context.bannerInstance || transformProperties(context.primary);
  const { reducedSpacing, marketingBannerFirstBlock } = context;

  let description = descriptionSource;
  if (isContentfulRichText(descriptionSource)) {
    description = descriptionSource.json;
  }

  const [bannerHeight, setBannerHeight] = useState(500);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isCaptchaDone, setIsCaptchaDone] = useState(!captcha);
  const reCaptchaRef = useRef(null);
  const bannerRef = useRef(null);

  const image = imageEntryToPrismicFormat(backgroundImageSource);
  const illustration = imageEntryToPrismicFormat(illustrationSource);

  const {
    submitText = 'download',
    inlineMessage: successMessage = '',
    formFieldGroups = [],
    submitUrl,
    hasRedirect,
    redirect,
  } = useHubspotForm(gatedFormId);

  const formFields = getFields(formFieldGroups);
  const { formState } = useFormEvents();

  useEffect(() => {
    setBannerHeight(bannerRef.current.clientHeight);
  }, []);

  async function handleSubmit(values, { setSubmitting }) {
    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    setShowLoader(true);

    if (isCaptchaDone) {
      try {
        await registerLead(
          {
            ...values,
            ...(Object.prototype.hasOwnProperty.call(values, 'utm')
              ? {
                  utm: getCurrentURL(),
                }
              : {}),
          },
          submitUrl
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
        });

        setShowLoader(false);
        setShowSuccessState(true);

        if (hasRedirect) {
          handleFormRedirect(redirect);
        }
      } catch (exception) {
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);
        throw exception;
      }
    }
  }

  const handleCaptchaChange = (value) => {
    setIsCaptchaDone(value);
  };

  return (
    <BannerPosition
      $sticky={size}
      $bannerHeight={bannerHeight}
      $marketingBannerFirstBlock={marketingBannerFirstBlock}
    >
      <BannerWrapper
        id={getIdForBanner(anchorId)}
        ref={bannerRef}
        $backgroundImage={image}
        $size={size}
        $reducedSpacing={reducedSpacing}
      >
        {contentfulFieldHasContent(title) && (
          <BannerHead>
            <BannerTitle>{title}</BannerTitle>
          </BannerHead>
        )}
        <BannerBody $illustration={illustration}>
          <BannerContent>
            {universalFieldHasContent(description) && (
              <BannerDescription $largeText={formAsModal}>
                <RenderFieldText field={description} />
              </BannerDescription>
            )}

            {formAsModal ? (
              <HubspotFormModalButton
                formId={gatedFormId}
                formTitle={title}
                formSubhead={description}
                gatedFile={file}
                gatedFileDownloadLabel={buttonLabel}
                as={CTAButton}
              >
                {buttonLabel || submitText}
              </HubspotFormModalButton>
            ) : (
              <FormWrapper>
                {showSuccessState ? (
                  <SuccessWrapper>
                    <SuccessMark />
                    <SuccessMessage>{successMessage || 'Thank you'}</SuccessMessage>
                    {hasRedirect && <SuccessMessage>Redirecting…</SuccessMessage>}
                    {!hasRedirect && (
                      <Button href={file.url} target={file.target}>
                        {buttonLabel}
                      </Button>
                    )}
                  </SuccessWrapper>
                ) : (
                  <ThemeProvider theme={{ mode: 'dark', ...mushuTheme }}>
                    <GlobalStyles />
                    {showFormError && (
                      <FormError>
                        Something went wrong. Please try again, or contact{' '}
                        <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline>{' '}
                        if this error persists. Thank you.
                      </FormError>
                    )}
                    <DynamicForm
                      formName="blog-banner-form"
                      fields={formFields}
                      fieldValues={formValues}
                      formError={formState.errorMessage}
                      handleOnSubmit={handleSubmit}
                      isLoading={formState.isLoading}
                      enableReinitialize
                    />
                    {captcha && (
                      <CaptchaWrapper>
                        <LocaleContextConsumer>
                          {(locale) => (
                            <ReCAPTCHA
                              hl={locale}
                              ref={reCaptchaRef}
                              sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                              onChange={handleCaptchaChange}
                            />
                          )}
                        </LocaleContextConsumer>
                      </CaptchaWrapper>
                    )}
                    <CTAButton
                      isLoading={showLoader}
                      form="blog-banner-form"
                      disabled={!isCaptchaDone}
                    >
                      {showLoader ? '' : submitText || buttonLabel}
                    </CTAButton>
                  </ThemeProvider>
                )}
              </FormWrapper>
            )}
          </BannerContent>
          {illustration && (
            <IllustrationWrapper>
              <Image
                src={illustration.url}
                alt={illustration.alt || ''}
                height={illustration.dimensions.height}
                width={illustration.dimensions.width}
                objectFit="contain"
                objectPosition="bottom right"
              />
            </IllustrationWrapper>
          )}
        </BannerBody>
      </BannerWrapper>
    </BannerPosition>
  );
}
